import type {
  SessionResponse,
  WidgetConfigResponse,
  WidgetPresenceResponse,
  ConversationResponse,
  MessageResponse,
  PaginatedMessages,
  SmsConsentRequest,
} from '../types';

export class MessagingApiClient {
  private apiUrl: string;
  private apiKey: string;
  private sessionToken: string | null = null;

  constructor(apiUrl: string, apiKey: string) {
    this.apiUrl = apiUrl.replace(/\/$/, '');
    this.apiKey = apiKey;
  }

  setSessionToken(token: string): void {
    this.sessionToken = token;
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-widget-key': this.apiKey,
      ...(options.headers as Record<string, string> || {}),
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(`${this.apiUrl}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new ApiError(response.status, errorBody || response.statusText);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // === Session ===

  async createSession(visitorId?: string): Promise<SessionResponse> {
    return this.request<SessionResponse>('/api/widget/session', {
      method: 'POST',
      body: JSON.stringify({ visitorId: visitorId || undefined }),
    });
  }

  // === Config ===

  async getWidgetConfig(): Promise<WidgetConfigResponse> {
    return this.request<WidgetConfigResponse>('/api/widget/config');
  }

  // === Presence ===

  /** Is any dashboard agent online for this product right now? (widget-key only) */
  async getPresence(): Promise<WidgetPresenceResponse> {
    return this.request<WidgetPresenceResponse>('/api/widget/presence');
  }

  // === Conversations ===

  async getConversations(): Promise<ConversationResponse[]> {
    return this.request<ConversationResponse[]>('/api/widget/conversations');
  }

  async startConversation(data: {
    subject: string;
    message: string;
    visitorName?: string;
    visitorEmail?: string;
    visitorPhone?: string;
  }): Promise<ConversationResponse> {
    return this.request<ConversationResponse>('/api/widget/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // === Messages ===

  async sendMessage(
    conversationId: string,
    content: string
  ): Promise<MessageResponse> {
    return this.request<MessageResponse>(
      `/api/widget/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify({ content, contentType: 'Text' }),
      }
    );
  }

  async getMessages(
    conversationId: string,
    page = 1,
    limit = 50
  ): Promise<PaginatedMessages> {
    return this.request<PaginatedMessages>(
      `/api/widget/conversations/${conversationId}/messages?page=${page}&limit=${limit}`
    );
  }

  // === SMS Consent ===

  async recordSmsConsent(data: SmsConsentRequest): Promise<{ consented: boolean }> {
    return this.request<{ consented: boolean }>('/api/widget/sms-consent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async smsOptOut(phone: string): Promise<{ optedOut: boolean }> {
    return this.request<{ optedOut: boolean }>('/api/widget/sms-opt-out', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  }

  async getSmsConsentStatus(phone: string): Promise<{ phone: string; isOptedIn: boolean }> {
    return this.request<{ phone: string; isOptedIn: boolean }>(
      `/api/widget/sms-consent?phone=${encodeURIComponent(phone)}`
    );
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string
  ) {
    super(`API error ${status}: ${body}`);
    this.name = 'ApiError';
  }
}
